import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@amobee/component-library';
import { compact, debounce, every, find, flatten, isEmpty, map, omitBy, reject, uniqBy } from 'lodash';
import ModalWithConfirmationButtons from '../../../../../common/Modal/ModalWithConfirmationButtons/ModalWithConfirmationButtons';
import Dropdown from '../../../../../common/Dropdown/Dropdown/Dropdown';
import DropdownWithHeader from '../../../../../common/Dropdown/DropdownWithHeader/DropdownWithHeader';
import TvCustomTimeframe from '../../TvCustomTimeframe/TvCustomTimeframe';
import Separator from '../../../../../common/Separator/Separator';
import ClickableIcon from '../../../../../common/ClickableIcon/ClickableIcon';
import LocalIcon from '../../../../../common/Icon/Icon';
import MultiLevelTableSelector from '../../../../../common/MultiLevelTableSelector/MultiLevelTableSelector';
import ResultsTable from '../../../../../common/ResultsTable/ResultsTable';
import SelectToggle from '../../../../../common/SelectToggle/SelectToggle';
import SingleSelectToggle from '../../../../../common/SingleSelectToggle/SingleSelectToggle';
import smallSpinnerGif from '../../../../../../../widgets/small_spinner.gif';
import {
  LEVELS_OF_EXPOSURE,
  LEVEL_OF_EXPOSURE_TV_SHOWS_TOOLTIP,
  VIEWING_TYPES,
  VIEWERSHIP_MODES,
  VIEWING_EVENT_QUALIFIERS,
  dateToString,
  getSummaryTextBuilder,
  CHANNELS,
  VIEWING_TYPES_MAP,
  COUNTRIES,
  UNIFIED_TV_SOURCES,
} from '../../../../../../../data/audience-segment-builder-helper';
import '../../../../../common/MultiLevelResultsTable/MultiLevelResultsTable.scss';
import { linkParents } from '../../../../../../utils/MultiLevelTableUtils';
import {
  getAdvancedTvNetworksMetaData,
  getAdvancedTvShowsBatch,
  searchAdvancedTvShows,
} from '../../../../../../services/AudienceInsightsService';

import './AdvancedTvShowsModal.scss';
import { isCanceledRequestError } from '../../../../../../infra/HttpClient';

const BATCH_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 1000;
const MIN_SEARCH_CHARS = 2;
const DATA_LEVELS = ['tv', 'seasons', 'episodes'];
const LEVEL_NAMES = ['TV Shows', 'Seasons', 'Episodes'];

const createEmptySelectedTvShows = () => ({
  tv: [],
  seasons: [],
  episodes: [],
});

export const buildSelectionKey = (level, value, source) =>
  source ? `${level}:${source}:${value}` : `${level}:${value}`;

const mergeSelectedTvShowsModels = (...models) =>
  models.reduce((merged, model = createEmptySelectedTvShows()) => {
    DATA_LEVELS.forEach((level) => {
      merged[level] = uniqBy([...(merged[level] || []), ...(model[level] || [])], 'selectionKey');
    });
    return merged;
  }, createEmptySelectedTvShows());

const normalizeSelectedShowItem = (level, item = {}) => {
  const rest = { ...item };
  const isMovieFromApi = rest['is_movie'] ?? rest.legacyIsMovie;
  const parentIdsFromApi = rest['parent_ids'];

  delete rest['is_movie'];
  delete rest.legacyIsMovie;
  delete rest['parent_ids'];

  return {
    ...rest,
    ...(item.isMovie !== undefined
      ? { isMovie: item.isMovie }
      : isMovieFromApi !== undefined
      ? { isMovie: isMovieFromApi }
      : {}),
    parentIds: item.parentIds || parentIdsFromApi || [],
    selectionKey: item.selectionKey || buildSelectionKey(level, item.value, item.source),
  };
};

const normalizeSelectedTvShows = (selectedTvShows = {}) =>
  DATA_LEVELS.reduce((normalized, level) => {
    normalized[level] = (selectedTvShows[level] || []).map((item) => normalizeSelectedShowItem(level, item));
    return normalized;
  }, createEmptySelectedTvShows());

const getTreeNodeSelectionKeys = (level, item = {}) => {
  const keys = [];

  if (item.selectionKey) {
    keys.push(item.selectionKey);
  }
  if (item.value !== undefined) {
    keys.push(buildSelectionKey(level, item.value, item.source));
  }
  if (level === 'seasons' && item.rawValue !== undefined) {
    keys.push(buildSelectionKey(level, item.rawValue, item.source));
  }

  return Array.from(new Set(keys.filter(Boolean)));
};

const createSelectionLookup = (selectedTvShows) =>
  DATA_LEVELS.reduce((lookup, level) => {
    lookup[level] = new Set();
    (selectedTvShows[level] || []).forEach((item) => {
      getTreeNodeSelectionKeys(level, item).forEach((selectionKey) => lookup[level].add(selectionKey));
    });
    return lookup;
  }, {});

const applySelectedTvShowsToTree = (
  treeData = [],
  selectedTvShows = createEmptySelectedTvShows(),
  { promoteParentsByChildren = true } = {}
) => {
  const selectedLookup = createSelectionLookup(selectedTvShows);

  const applySelection = (items = [], level = 0, isParentSelected = false) =>
    items.map((item) => {
      const levelKey = DATA_LEVELS[level];
      const isExplicitlySelected =
        isParentSelected ||
        getTreeNodeSelectionKeys(levelKey, item).some((selectionKey) => selectedLookup[levelKey].has(selectionKey));
      const children = item.children?.length
        ? applySelection(item.children, level + 1, isExplicitlySelected)
        : item.children;
      const isSelectedByChildren = promoteParentsByChildren && !!children?.length && every(children, 'selected');

      return {
        ...item,
        children,
        selected: isExplicitlySelected || isSelectedByChildren,
      };
    });

  return applySelection(treeData);
};

const arraysEqual = (first = [], second = []) =>
  first.length === second.length && first.every((value, index) => value === second[index]);

const getNodeParentIds = (item = {}) => item.parentIds || item['parent_ids'] || [];

// Two copies of the same id can coexist in a unified TV tree (one per source), so id+parent_ids
// alone are ambiguous. Sources must match when both nodes carry one; nodes without a source
// (non-unified channels, legacy saved items) keep the id-only behavior.
const isSameNodeSource = (item, targetNode) =>
  item.source === undefined || targetNode.source === undefined || item.source === targetNode.source;

const isAncestorOfTargetNode = (item, targetNode) => {
  const itemParentIds = getNodeParentIds(item);
  const targetParentIds = getNodeParentIds(targetNode);

  return (
    isSameNodeSource(item, targetNode) &&
    itemParentIds.length < targetParentIds.length &&
    arraysEqual(itemParentIds, targetParentIds.slice(0, itemParentIds.length)) &&
    targetParentIds[itemParentIds.length] === item.value
  );
};

const isTargetTreeNode = (item, targetNode) =>
  item.value === targetNode.value &&
  isSameNodeSource(item, targetNode) &&
  arraysEqual(getNodeParentIds(item), getNodeParentIds(targetNode));

const updateChildSelectionState = (items = [], selected) =>
  items.map((item) => ({
    ...item,
    selected,
    children: item.children?.length ? updateChildSelectionState(item.children, selected) : item.children,
  }));

const applyExplicitSelectionToTree = (treeData = [], targetNode) => {
  if (!targetNode || targetNode.isSelectAll) {
    return treeData;
  }

  const updateTree = (items = []) =>
    items.map((item) => {
      const children = item.children?.length ? updateTree(item.children) : item.children;

      if (isTargetTreeNode(item, targetNode)) {
        return {
          ...item,
          selected: targetNode.selected,
          children: children?.length ? updateChildSelectionState(children, targetNode.selected) : children,
        };
      }

      if (!targetNode.selected && isAncestorOfTargetNode(item, targetNode)) {
        return {
          ...item,
          selected: false,
          children,
        };
      }

      return {
        ...item,
        children,
      };
    });

  return updateTree(treeData);
};

const collectVisibleSelectionKeys = (treeData = []) => {
  const visibleKeys = DATA_LEVELS.reduce((result, level) => {
    result[level] = new Set();
    return result;
  }, {});

  const collectKeys = (items = [], level = 0) => {
    items.forEach((item) => {
      const levelKey = DATA_LEVELS[level];
      getTreeNodeSelectionKeys(levelKey, item).forEach((selectionKey) => visibleKeys[levelKey].add(selectionKey));
      if (item.children?.length) {
        collectKeys(item.children, level + 1);
      }
    });
  };

  collectKeys(treeData);
  return visibleKeys;
};

const collectSelectedTvShowsFromTree = (treeData = [], { episodesOnly = false } = {}) => {
  const selectedTvShows = createEmptySelectedTvShows();

  const collectSelected = (items = [], level = 0, parentContext = {}) => {
    items.forEach((item) => {
      const nextParentContext = {
        ...parentContext,
        ...(level === 0 && { tvName: item.label }),
        ...(level === 1 && { seasonName: item.label }),
      };

      if (!item.selected && item.children?.length) {
        collectSelected(item.children, level + 1, nextParentContext);
      }

      if (item.selected) {
        if (episodesOnly && item.children?.length) {
          // Partial-tree mode (search scope = episodes / all): the visible tree only shows matched
          // episodes, so selecting a parent must NOT store the parent — it stands for "the episodes
          // shown beneath it", not the entire series. Descend until we reach leaf episodes.
          collectSelected(item.children, level + 1, nextParentContext);
          return;
        }
        const levelKey = DATA_LEVELS[level];
        selectedTvShows[levelKey] = uniqBy(
          [
            ...selectedTvShows[levelKey],
            normalizeSelectedShowItem(levelKey, {
              isMovie: item.isMovie,
              legacyIsMovie: item['is_movie'],
              value: item.value,
              label: item.label,
              source: item.source,
              children: item.children || [],
              parentIds: item['parent_ids'] || [],
              ...(parentContext.tvName && { tvName: parentContext.tvName }),
              ...(parentContext.seasonName && { seasonName: parentContext.seasonName }),
            }),
          ],
          'selectionKey'
        );
      }
    });
  };

  collectSelected(treeData);
  return selectedTvShows;
};

const areAllTreeNodesSelected = (items = []) =>
  items.length > 0 &&
  every(items, (item) => !!item.selected && (!item.children?.length || areAllTreeNodesSelected(item.children)));

const isExplicitLegacyTvSelection = (item = {}) =>
  !item.children?.length || !!item.selected || areAllTreeNodesSelected(item.children);

const pruneSelectedTvShows = (selectedTvShows) => {
  // value → Set of sources it was selected under. Unified TV holds two copies of the same id
  // (one per source), so a bare id match would let a fully-selected hisense series erase the
  // peerlogix selections that share its id. `undefined` acts as a wildcard for legacy items.
  const collectValueSources = (items = []) => {
    const valueSources = new Map();
    items.forEach(({ value, source }) => {
      if (!valueSources.has(value)) valueSources.set(value, new Set());
      valueSources.get(value).add(source);
    });
    return valueSources;
  };
  const hasMatchingSource = (valueSources, value, source) => {
    const sources = valueSources.get(value);
    if (!sources) return false;
    return source === undefined || sources.has(undefined) || sources.has(source);
  };

  const selectedTvValues = collectValueSources(selectedTvShows.tv);
  const selectedTvNames = new Set((selectedTvShows.tv || []).map(({ label }) => label));
  const selectedSeasonValues = collectValueSources(selectedTvShows.seasons);
  const selectedSeasonNames = new Set((selectedTvShows.seasons || []).map(({ label }) => label));

  // Label-based fallback applies only when the item lacks parentIds (legacy saved segments).
  // For items that carry parentIds, the unambiguous series_id check is the only one we trust —
  // otherwise two distinct series that share a label would erase each other's selections.
  const isPrunedBySeries = (item) => {
    const parentSeriesId = item.parentIds?.[0];
    if (parentSeriesId !== undefined) {
      return hasMatchingSource(selectedTvValues, parentSeriesId, item.source);
    }
    return selectedTvNames.has(item.tvName);
  };

  const isPrunedBySeason = (episode) => {
    const parentSeasonId = episode.parentIds?.[episode.parentIds.length - 1];
    if (parentSeasonId !== undefined && episode.parentIds.length > 1) {
      return hasMatchingSource(selectedSeasonValues, parentSeasonId, episode.source);
    }
    return selectedSeasonNames.has(episode.seasonName);
  };

  return {
    tv: selectedTvShows.tv || [],
    seasons: (selectedTvShows.seasons || []).filter((season) => !isPrunedBySeries(season)),
    episodes: (selectedTvShows.episodes || []).filter(
      (episode) => !isPrunedBySeries(episode) && !isPrunedBySeason(episode)
    ),
  };
};

const mergeSelectedTvShowsWithTree = (selectedTvShows, treeData, { episodesOnly = false } = {}) => {
  const visibleSelectionKeys = collectVisibleSelectionKeys(treeData);
  const treeSelectedTvShows = collectSelectedTvShowsFromTree(treeData, { episodesOnly });

  return pruneSelectedTvShows(
    DATA_LEVELS.reduce((merged, level) => {
      merged[level] = uniqBy(
        [
          ...(selectedTvShows[level] || []).filter(
            (item) =>
              !getTreeNodeSelectionKeys(level, item).some((selectionKey) =>
                visibleSelectionKeys[level].has(selectionKey)
              )
          ),
          ...(treeSelectedTvShows[level] || []),
        ],
        'selectionKey'
      );
      return merged;
    }, createEmptySelectedTvShows())
  );
};

const buildInitialSelectedTvShows = (tvShowsInput = {}) =>
  pruneSelectedTvShows(
    mergeSelectedTvShowsModels(
      normalizeSelectedTvShows({
        tv: (tvShowsInput.tv || []).filter(isExplicitLegacyTvSelection),
        seasons: tvShowsInput.seasons || [],
        episodes: tvShowsInput.episodes || [],
      }),
      collectSelectedTvShowsFromTree(tvShowsInput.tv || [])
    )
  );

const countDescendantsByLevel = (item, levelIndex) => {
  const descendants = {};

  const countDescendantsRecursive = (children = [], currentLevel = levelIndex) => {
    if (!children.length || currentLevel >= LEVEL_NAMES.length - 1) {
      return;
    }

    const childLevelName = LEVEL_NAMES[currentLevel + 1];
    descendants[childLevelName] = (descendants[childLevelName] || 0) + children.length;
    children.forEach((child) => countDescendantsRecursive(child.children || [], currentLevel + 1));
  };

  countDescendantsRecursive(item.children || []);
  return Object.entries(descendants)
    .map(([levelName, count]) => `${count} ${levelName}`)
    .join(' | ');
};

const getSelectedShowSecondaryText = (level, item) => {
  switch (level) {
    case 'tv':
      return countDescendantsByLevel(item, 0);
    case 'seasons':
      return compact([item.tvName, countDescendantsByLevel(item, 1)]).join(' | ');
    case 'episodes':
      return compact([item.tvName, item.seasonName]).join(' > ');
    default:
      return '';
  }
};

const AdvancedTvShowsModal = ({
  isOpen,
  modalTitle,
  onSubmit,
  onCancel,
  tvShowsInput,
  tvGenresMetadataPromise,
  channel,
  geo,
}) => {
  const MODAL_HEIGHT = '890px';
  const MIN_VIEWED_PROGRAMS = 1;
  const MAX_VIEWED_PROGRAMS = 1000;
  const DEFAULT_VIEWING_TYPE_BY_CHANNEL = {
    tivo: { default: VIEWING_TYPES_MAP.linear?.value },
    peerlogix: { default: VIEWING_TYPES_MAP.streaming?.value },
    hisense: { default: VIEWING_TYPES_MAP.both?.value },
    unified_tv: { default: VIEWING_TYPES_MAP.both?.value },
  };

  const geoParam = CHANNELS[channel]?.isGeoMandatory ? geo : '';
  const isHisense = channel === CHANNELS.hisense?.name;
  const isUnifiedTv = channel === CHANNELS.unified_tv?.name;

  const isMountedRef = useRef(true);
  const prefetchInFlightRef = useRef(false);
  const latestSearchRequestRef = useRef(0);
  const latestBatchRequestRef = useRef(0);
  const searchPaginationInFlightRef = useRef(false);
  const skipNextFetchRef = useRef(false);

  // --- Loading states ---
  const [isLoadingFirstBatch, setIsLoadingFirstBatch] = useState(true);
  const [isLoadingMoreBatches, setIsLoadingMoreBatches] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);

  // --- Tree data ---
  const [batchedTreeData, setBatchedTreeData] = useState([]);
  const [searchTreeData, setSearchTreeData] = useState(null);

  // --- Pagination ---
  const [hasMore, setHasMore] = useState(false);
  const hasMoreRef = useRef(false);
  const currentOffsetRef = useRef(0);
  const searchHasMoreRef = useRef(false);
  const currentSearchOffsetRef = useRef(0);

  // --- Search ---
  const [searchValue, setSearchValue] = useState('');
  const [activeSearchValue, setActiveSearchValue] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchScope, setSearchScope] = useState('all');
  const [exactMatch, setExactMatch] = useState(false);
  const searchScopeRef = useRef(searchScope);
  const exactMatchRef = useRef(exactMatch);
  searchScopeRef.current = searchScope;
  exactMatchRef.current = exactMatch;

  // --- Other modal state ---
  const [viewingType, setViewingType] = useState(
    tvShowsInput.viewingType ||
      DEFAULT_VIEWING_TYPE_BY_CHANNEL[channel][geoParam] ||
      DEFAULT_VIEWING_TYPE_BY_CHANNEL[channel].default ||
      'both'
  );
  const [selectedNetworks, setSelectedNetworks] = useState(tvShowsInput.networks || []);
  const [selectedTvShows, setSelectedTvShows] = useState(() => buildInitialSelectedTvShows(tvShowsInput));
  const [selectedGenres, setSelectedGenres] = useState(tvShowsInput.genres || []);
  const [selectedStartDate, setSelectedStartDate] = useState(tvShowsInput.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(tvShowsInput.endDate);
  const [selectedTimeframe, setSelectedTimeframe] = useState(tvShowsInput.timeframe);
  const [isTimeframeDisabled, setIsTimeframeDisabled] = useState(
    isEmpty(tvShowsInput.networks) &&
      isEmpty(tvShowsInput.tv) &&
      isEmpty(tvShowsInput.episodes) &&
      isEmpty(tvShowsInput.genres)
  );
  const [levelOfExposure, setLevelOfExposure] = useState(tvShowsInput.exposure);
  const [viewershipModes, setViewershipModes] = useState(tvShowsInput.viewModes || []);
  const [viewEventQualifier, setViewEventQualifier] = useState(
    tvShowsInput.viewEventQual || isHisense ? VIEWING_EVENT_QUALIFIERS[0] : VIEWING_EVENT_QUALIFIERS[1]
  );
  const [minNumberOfViewedProgs, setMinNumberOfViewedProgs] = useState(tvShowsInput.minViewedProgs || '');

  const seedSelectedSources = () => {
    const seed = tvShowsInput.sources;
    if (!seed?.length) return UNIFIED_TV_SOURCES;
    const seedSet = new Set(seed);
    const filtered = UNIFIED_TV_SOURCES.filter((s) => seedSet.has(s.value));
    return filtered.length ? filtered : UNIFIED_TV_SOURCES;
  };
  const [selectedSources, setSelectedSources] = useState(isUnifiedTv ? seedSelectedSources() : []);
  const [isSourceMenuOpen, setIsSourceMenuOpen] = useState(false);
  const sourceMenuRef = useRef(null);
  const sourcesParam = useMemo(
    () => (isUnifiedTv ? map(selectedSources, 'value') : undefined),
    [isUnifiedTv, selectedSources]
  );

  const isStreamingOnly = isUnifiedTv && selectedSources.length === 1 && selectedSources[0].value === 'peerlogix';
  const viewingTypeOptions = VIEWING_TYPES.map((type) =>
    type.value === VIEWING_TYPES_MAP.linear?.value && isStreamingOnly ? { ...type, disabled: true } : type
  );

  useEffect(() => {
    if (isStreamingOnly && viewingType === VIEWING_TYPES_MAP.linear?.value) {
      setViewingType(DEFAULT_VIEWING_TYPE_BY_CHANNEL.unified_tv.default);
    }
  }, [isStreamingOnly, viewingType]);

  const isHisenseGermanyLinear = isHisense && geoParam === COUNTRIES.DE?.cc?.toLowerCase() && viewingType === 'linear';
  const isHisenseGermanyBoth = isHisense && geoParam === COUNTRIES.DE?.cc?.toLowerCase() && viewingType === 'both';
  const selectedTvShowsRef = useRef(selectedTvShows);

  const NUM_PROGS_PATTERN = '([0-9]{0,3})|(' + MAX_VIEWED_PROGRAMS + ')';

  const tvNetworksMetadataPromise = useMemo(
    channel && viewingType
      ? () => getAdvancedTvNetworksMetaData(`${channel}_${viewingType}`, geoParam)
      : Promise.resolve([]),
    [channel, viewingType, geoParam]
  );

  const changeIsTimeframeDisabled = useCallback(
    ({ networks = selectedNetworks, genres = selectedGenres, shows = selectedTvShows }) => {
      const showsIsEmpty = !shows || Object.values(shows).every((items) => items.length === 0);
      const isTimeframeDisabledCurrent = isEmpty(networks) && isEmpty(genres) && showsIsEmpty;
      if (isTimeframeDisabledCurrent !== isTimeframeDisabled) setIsTimeframeDisabled(isTimeframeDisabledCurrent);
    },
    [isTimeframeDisabled, selectedGenres, selectedNetworks, selectedTvShows]
  );

  const syncLoadedTreesWithSelectedTvShows = useCallback((nextSelectedTvShows) => {
    setBatchedTreeData((prev) => applySelectedTvShowsToTree(prev, nextSelectedTvShows));
    setSearchTreeData((prev) => {
      if (prev === null) {
        return prev;
      }
      return applySelectedTvShowsToTree(prev, nextSelectedTvShows, { promoteParentsByChildren: false });
    });
  }, []);

  const applySelectedTvShowsState = useCallback(
    (nextSelectedTvShows) => {
      const normalizedSelectedTvShows = pruneSelectedTvShows(normalizeSelectedTvShows(nextSelectedTvShows));
      selectedTvShowsRef.current = normalizedSelectedTvShows;
      setSelectedTvShows(normalizedSelectedTvShows);
      syncLoadedTreesWithSelectedTvShows(normalizedSelectedTvShows);
      changeIsTimeframeDisabled({ shows: normalizedSelectedTvShows });
    },
    [changeIsTimeframeDisabled, syncLoadedTreesWithSelectedTvShows]
  );

  // --- Tree building ---

  const getSourceColorClass = (source) =>
    find(UNIFIED_TV_SOURCES, { value: source })?.colorClass || 'tv-source-hisense';

  const sourceDot = (source) => {
    if (!isUnifiedTv || !source) return undefined;
    return <span className={`tv-source-dot ${getSourceColorClass(source)}`} />;
  };

  const buildTreeNodes = (tvShowsMetadata, { isPartialTree = false } = {}) => {
    const tvShowsTree = [];
    if (tvShowsMetadata?.length) {
      tvShowsMetadata.map((showMetadata) => {
        const label = isHisense && showMetadata.id ? `${showMetadata.title} (${showMetadata.id})` : showMetadata.title;
        const show = {
          value: showMetadata.id,
          label,
          isMovie: showMetadata['is_movie'],
          selected: false,
          source: showMetadata.source,
          leadingAdornment: sourceDot(showMetadata.source),
        };
        if (!showMetadata['is_movie'] && showMetadata.seasons?.length) {
          show.children = showMetadata.seasons.map((seasonMetadata) => {
            const season = {
              value: `${showMetadata.id}-${seasonMetadata.id}`,
              rawValue: seasonMetadata.id,
              label: seasonMetadata.title,
              parentId: showMetadata.id,
              isSeason: seasonMetadata.isSeason,
              selected: false,
              source: showMetadata.source,
              leadingAdornment: sourceDot(showMetadata.source),
            };
            if (seasonMetadata.episodes?.length) {
              const sortedEpisodes = [...seasonMetadata.episodes].sort((a, b) => {
                const aNum = a.episodeNumber ?? a.number ?? null;
                const bNum = b.episodeNumber ?? b.number ?? null;
                if (aNum != null && bNum != null) return aNum - bNum;
                const titleCompare = String(a.title || '').localeCompare(String(b.title || ''));
                if (titleCompare !== 0) return titleCompare;
                if (a.date && b.date) return String(a.date).localeCompare(String(b.date));
                if (a.id && b.id) return String(a.id).localeCompare(String(b.id));
                return 0;
              });
              season.children = sortedEpisodes.map((episodeMetadata) => {
                const epLabel =
                  isHisense && episodeMetadata.id
                    ? `${episodeMetadata.title} (${episodeMetadata.id})`
                    : episodeMetadata.title;
                return {
                  value: episodeMetadata.id,
                  label: epLabel,
                  parentId: season.value,
                  selected: false,
                  source: episodeMetadata.source,
                  leadingAdornment: sourceDot(episodeMetadata.source),
                };
              });
              show.label = showMetadata.title;
            }
            return season;
          });
        }
        tvShowsTree.push(show);
      });
    }
    tvShowsTree.forEach((element) => linkParents(element));
    return applySelectedTvShowsToTree(tvShowsTree, selectedTvShowsRef.current, {
      promoteParentsByChildren: !isPartialTree,
    });
  };

  // A show is uniquely a (source, id): the unified TV batch/pagination can return the same one more
  // than once, which would render duplicate rows and duplicate React keys. Keep the first occurrence
  // (source-less nodes dedupe on id alone, which is correct for single-source channels too).
  const dedupeShowNodes = (nodes) => uniqBy(nodes, (node) => `${node.source || ''}:${node.value}`);

  // --- Batch loading ---

  const prefetchNextBatch = useCallback(async () => {
    const offset = currentOffsetRef.current;
    if (prefetchInFlightRef.current || !hasMoreRef.current) {
      return;
    }
    // Capture (without bumping) the current batch request id: if fetchFirstBatch supersedes this
    // request while the prefetch is in flight, the id will have moved on and we must bail below.
    const batchRequestId = latestBatchRequestRef.current;
    prefetchInFlightRef.current = true;
    setIsLoadingMoreBatches(true);
    try {
      const result = await getAdvancedTvShowsBatch(
        channel,
        viewingType,
        map(selectedNetworks, 'value'),
        map(selectedGenres, 'value'),
        geoParam,
        offset,
        BATCH_SIZE,
        sourcesParam
      );
      if (!isMountedRef.current || batchRequestId !== latestBatchRequestRef.current) return;
      const newNodes = buildTreeNodes(result.shows);
      setBatchedTreeData((prev) => dedupeShowNodes([...prev, ...newNodes]));
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
      currentOffsetRef.current = offset + BATCH_SIZE;
    } catch (error) {
      if (isCanceledRequestError(error) || !isMountedRef.current || batchRequestId !== latestBatchRequestRef.current) {
        return;
      }
    } finally {
      prefetchInFlightRef.current = false;
      if (isMountedRef.current) setIsLoadingMoreBatches(false);
    }
  }, [channel, viewingType, selectedNetworks, selectedGenres, geoParam, sourcesParam]);

  const resetSearchPagination = useCallback(() => {
    searchHasMoreRef.current = false;
    currentSearchOffsetRef.current = 0;
    searchPaginationInFlightRef.current = false;
    setIsLoadingMoreBatches(false);
  }, []);

  const fetchFirstBatch = useCallback(async () => {
    if (!isMountedRef.current) return;
    setIsLoadingFirstBatch(true);
    setHasApiError(false);
    setBatchedTreeData([]);
    setSearchTreeData(null);
    setSearchValue('');
    setActiveSearchValue('');
    setIsSearchMode(false);
    setIsSearching(false);
    resetSearchPagination();
    currentOffsetRef.current = 0;
    hasMoreRef.current = false;
    setHasMore(false);
    prefetchInFlightRef.current = false;
    latestSearchRequestRef.current += 1;
    // Monotonic request-id guard (mirrors performSearch's latestSearchRequestRef): a slower earlier
    // fetchFirstBatch response must not overwrite a newer one (e.g. removing a source chip triggers
    // a fetch, and the viewing-type fallback effect can trigger a second one right after).
    latestBatchRequestRef.current += 1;
    const batchRequestId = latestBatchRequestRef.current;

    try {
      const result = await getAdvancedTvShowsBatch(
        channel,
        viewingType,
        map(selectedNetworks, 'value'),
        map(selectedGenres, 'value'),
        geoParam,
        0,
        BATCH_SIZE,
        sourcesParam
      );
      if (!isMountedRef.current || batchRequestId !== latestBatchRequestRef.current) return;
      const firstNodes = dedupeShowNodes(buildTreeNodes(result.shows));
      setBatchedTreeData(firstNodes);
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
      currentOffsetRef.current = BATCH_SIZE;
      setIsLoadingFirstBatch(false);
    } catch (error) {
      if (isCanceledRequestError(error) || !isMountedRef.current || batchRequestId !== latestBatchRequestRef.current) {
        return;
      }
      setIsLoadingFirstBatch(false);
      setHasApiError(true);
    }
  }, [channel, viewingType, selectedNetworks, selectedGenres, geoParam, resetSearchPagination, sourcesParam]);

  useEffect(() => {
    isMountedRef.current = true;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
    } else {
      fetchFirstBatch();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [selectedNetworks, selectedGenres, viewingType, selectedSources]);

  useEffect(() => {
    if (!isLoadingFirstBatch && hasMore && !isSearchMode) {
      prefetchNextBatch();
    }
  }, [hasMore, isLoadingFirstBatch]);

  // --- Scroll handler ---

  const performSearch = useCallback(
    async (query, offset = 0, appendResults = false) => {
      if (
        !isMountedRef.current ||
        (appendResults && (searchPaginationInFlightRef.current || !searchHasMoreRef.current))
      ) {
        return;
      }

      const requestId = latestSearchRequestRef.current + 1;
      latestSearchRequestRef.current = requestId;

      if (appendResults) {
        searchPaginationInFlightRef.current = true;
        setIsLoadingMoreBatches(true);
      } else {
        setActiveSearchValue(query);
        setIsSearching(true);
      }

      try {
        const result = await searchAdvancedTvShows(
          channel,
          viewingType,
          map(selectedNetworks, 'value'),
          map(selectedGenres, 'value'),
          geoParam,
          query,
          offset,
          BATCH_SIZE,
          searchScopeRef.current,
          exactMatchRef.current,
          sourcesParam
        );
        if (!isMountedRef.current || requestId !== latestSearchRequestRef.current) return;
        const searchNodes = buildTreeNodes(result.shows, { isPartialTree: true });
        setSearchTreeData((prev) => dedupeShowNodes(appendResults ? [...(prev || []), ...searchNodes] : searchNodes));
        searchHasMoreRef.current = result.hasMore;
        currentSearchOffsetRef.current = offset + BATCH_SIZE;
      } catch (error) {
        if (isCanceledRequestError(error) || !isMountedRef.current || requestId !== latestSearchRequestRef.current) {
          return;
        }
        searchHasMoreRef.current = false;
        currentSearchOffsetRef.current = 0;
        if (!appendResults) {
          setSearchTreeData([]);
        }
      } finally {
        if (appendResults) {
          searchPaginationInFlightRef.current = false;
          if (isMountedRef.current) setIsLoadingMoreBatches(false);
        } else if (isMountedRef.current && requestId === latestSearchRequestRef.current) {
          setIsSearching(false);
        }
      }
    },
    [channel, viewingType, selectedNetworks, selectedGenres, geoParam, sourcesParam]
  );

  // --- Search ---

  const fetchNextSearchPage = useCallback(
    () => performSearch(activeSearchValue, currentSearchOffsetRef.current, true),
    [performSearch, activeSearchValue]
  );

  const debouncedSearch = useCallback(debounce(performSearch, SEARCH_DEBOUNCE_MS), [performSearch]);

  const handleScrolledToBottom = useCallback(() => {
    if (isLoadingMoreBatches) {
      return;
    }
    if (isSearchMode) {
      fetchNextSearchPage();
      return;
    }
    if (hasMoreRef.current) {
      prefetchNextBatch();
    }
  }, [isLoadingMoreBatches, isSearchMode, fetchNextSearchPage, prefetchNextBatch]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);
      latestSearchRequestRef.current += 1;
      debouncedSearch.cancel();
      if (value.length < MIN_SEARCH_CHARS) {
        resetSearchPagination();
        setIsSearchMode(false);
        setIsSearching(false);
        setSearchTreeData(null);
        setActiveSearchValue('');
        return;
      }
      resetSearchPagination();
      setIsSearchMode(true);
      setIsSearching(true);
      setSearchTreeData(null);
      setActiveSearchValue('');
      debouncedSearch(value, 0, false);
    },
    [debouncedSearch, resetSearchPagination]
  );

  const handleClearSearch = useCallback(() => {
    latestSearchRequestRef.current += 1;
    resetSearchPagination();
    setSearchValue('');
    setActiveSearchValue('');
    setIsSearchMode(false);
    setIsSearching(false);
    setSearchTreeData(null);
    debouncedSearch.cancel();
  }, [debouncedSearch, resetSearchPagination]);

  const reissueActiveSearch = useCallback(() => {
    if (!isSearchMode || activeSearchValue.length < MIN_SEARCH_CHARS) return;
    latestSearchRequestRef.current += 1;
    debouncedSearch.cancel();
    resetSearchPagination();
    setIsSearching(true);
    setSearchTreeData(null);
    performSearch(activeSearchValue, 0, false);
  }, [activeSearchValue, debouncedSearch, isSearchMode, performSearch, resetSearchPagination]);

  const handleSearchScopeChange = useCallback(
    (nextScope) => {
      setSearchScope(nextScope);
      searchScopeRef.current = nextScope;
      reissueActiveSearch();
    },
    [reissueActiveSearch]
  );

  const handleExactMatchChange = useCallback(
    (nextExactMatch) => {
      setExactMatch(nextExactMatch);
      exactMatchRef.current = nextExactMatch;
      reissueActiveSearch();
    },
    [reissueActiveSearch]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // --- Display tree (search results or batched data) ---

  const displayTreeData = isSearchMode ? searchTreeData || [] : batchedTreeData;

  // --- Selection ---

  const resetAllSelected = () => {
    setSelectedNetworks([]);
    setSelectedGenres([]);
    setBatchedTreeData([]);
    setSearchTreeData(null);
    applySelectedTvShowsState(createEmptySelectedTvShows());
    setIsTimeframeDisabled(true);
    setSelectedStartDate(undefined);
    setSelectedEndDate(undefined);
    setSelectedTimeframe(undefined);
    setLevelOfExposure([]);
    setViewershipModes([]);
    setViewEventQualifier(isHisense ? VIEWING_EVENT_QUALIFIERS[0] : VIEWING_EVENT_QUALIFIERS[1]);
    setMinNumberOfViewedProgs('');
  };

  const onChangeViewingType = (type) => {
    resetAllSelected();
    setViewingType(type);
  };

  const onResetSelectedTvShows = () => {
    applySelectedTvShowsState(createEmptySelectedTvShows());
    handleClearSearch();
  };

  const onNetworksSelect = (networks) => {
    setSelectedNetworks(networks);
    changeIsTimeframeDisabled({ networks });
  };

  const onGenresSelect = (genres) => {
    setSelectedGenres(genres);
    changeIsTimeframeDisabled({ genres });
  };

  const onRemoveSource = (sourceValue) => {
    if (selectedSources.length <= 1) return;
    // Intentional (product decision): removing a source only filters future load/search — it does
    // NOT prune already-selected items belonging to that source. The submitted segment may
    // therefore include tv/season/episode items whose `source` is not present in `sources`. Do not
    // "fix" this by pruning selectedTvShows here.
    setSelectedSources((prev) => prev.filter((s) => s.value !== sourceValue));
  };

  const onToggleSource = (sourceValue) => {
    const isSelected = selectedSources.some((source) => source.value === sourceValue);
    if (isSelected) {
      // onRemoveSource enforces "at least one source" — removing the last one is a no-op.
      onRemoveSource(sourceValue);
      return;
    }
    // Re-map through UNIFIED_TV_SOURCES so chip order and the `sources` payload stay canonical.
    const nextValues = new Set([...selectedSources.map((source) => source.value), sourceValue]);
    setSelectedSources(UNIFIED_TV_SOURCES.filter((source) => nextValues.has(source.value)));
  };

  useEffect(() => {
    if (!isSourceMenuOpen) return undefined;
    const onDocumentMouseDown = (event) => {
      if (sourceMenuRef.current && !sourceMenuRef.current.contains(event.target)) {
        setIsSourceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isSourceMenuOpen]);

  const onShowsDataTableSelect = (data, selectedShow) => {
    if (isSearchMode) {
      const explicitSearchData = applyExplicitSelectionToTree(
        applySelectedTvShowsToTree(data, selectedTvShowsRef.current, { promoteParentsByChildren: false }),
        selectedShow
      );
      setSearchTreeData(explicitSearchData);
      // When search scope is "series", matched series have ALL their episodes returned, so
      // selecting a parent legitimately means "the whole series". For scope = "episodes"/"all",
      // the tree only shows matched episodes — selecting a parent represents only those visible
      // episodes, so we collect at the leaf level.
      const isPartialTree = searchScope !== 'series';
      applySelectedTvShowsState(
        mergeSelectedTvShowsWithTree(selectedTvShowsRef.current, explicitSearchData, { episodesOnly: isPartialTree })
      );
      return;
    }

    setBatchedTreeData([...data]);
    applySelectedTvShowsState(mergeSelectedTvShowsWithTree(selectedTvShowsRef.current, data));
  };

  const resultsTable = useMemo(
    () =>
      compact([
        !isEmpty(selectedNetworks) && {
          type: 'networks',
          title: 'Networks',
          results: selectedNetworks,
        },
        !isEmpty(selectedGenres) && {
          type: 'genres',
          title: 'Genres',
          results: selectedGenres,
        },
      ]),
    [selectedNetworks, selectedGenres]
  );

  const onRemoveResultsTable = (item, type) => {
    const resultsWrapper = find(resultsTable, { type });
    const selectedResults = reject(resultsWrapper.results, { value: item.value });
    switch (type) {
      case 'networks':
        setSelectedNetworks(selectedResults);
        changeIsTimeframeDisabled({ networks: selectedResults });
        break;
      case 'genres':
        setSelectedGenres(selectedResults);
        changeIsTimeframeDisabled({ genres: selectedResults });
        break;
    }
  };

  const onResetResultsTable = () => {
    const hasSelection = selectedNetworks.length > 0 || selectedGenres.length > 0 || selectedTvShowsSections.length > 0;
    if (!hasSelection) return;
    // Only skip the re-fetch when no filter criteria are being cleared.
    // When genres/networks are cleared, we want a fresh unfiltered fetch.
    if (selectedNetworks.length === 0 && selectedGenres.length === 0) {
      skipNextFetchRef.current = true;
    }
    tvShowsInput.episodes = [];
    tvShowsInput.tv = [];
    applySelectedTvShowsState(createEmptySelectedTvShows());
    handleClearSearch();
    setSelectedNetworks([]);
    setSelectedGenres([]);
    setIsTimeframeDisabled(true);
    setSelectedStartDate(undefined);
    setSelectedEndDate(undefined);
    setSelectedTimeframe(undefined);
    setLevelOfExposure([]);
    setViewershipModes([]);
    setViewEventQualifier(isHisense ? VIEWING_EVENT_QUALIFIERS[0] : VIEWING_EVENT_QUALIFIERS[1]);
    setMinNumberOfViewedProgs('');
  };

  const onMinNumProgsChange = ({ target: { value, validity } }) => {
    if (validity?.valid && (value === '' || (value >= MIN_VIEWED_PROGRAMS && value <= MAX_VIEWED_PROGRAMS))) {
      setMinNumberOfViewedProgs(value);
    }
  };

  const onRemoveSelectedTvShow = (level, item) => {
    const nextSelectedTvShows = {
      ...selectedTvShowsRef.current,
      [level]: reject(selectedTvShowsRef.current[level], { selectionKey: item.selectionKey }),
    };
    applySelectedTvShowsState(nextSelectedTvShows);
  };

  const selectedTvShowsSections = useMemo(
    () =>
      DATA_LEVELS.map((level, index) => ({
        level,
        title: LEVEL_NAMES[index],
        items: selectedTvShows[level],
      })).filter(({ items }) => items.length > 0),
    [selectedTvShows]
  );

  const handleSubmit = useCallback(() => {
    // Strips only `leadingAdornment` (a non-serializable React element) recursively through
    // children, preserving all other fields so non-unified nested children stay byte-for-byte
    // unchanged. item.children may hold a raw subtree (seasons/episodes) that is otherwise stored
    // as-is, so every level must be walked to remove leadingAdornment without touching anything else.
    const stripLeadingAdornmentDeep = ({ leadingAdornment, ...node }) => ({
      ...node,
      ...(node.children?.length ? { children: node.children.map(stripLeadingAdornmentDeep) } : {}),
    });
    const sanitizeSelectedShow = ({ selectionKey, rawValue, isMovie, parentIds, leadingAdornment, ...item }) => {
      const sanitizedItem = { ...item };

      if (isMovie !== undefined) {
        sanitizedItem['is_movie'] = isMovie;
      }
      if (item.children?.length) {
        sanitizedItem.children = item.children.map(stripLeadingAdornmentDeep);
      }

      return sanitizedItem;
    };
    const tv = selectedTvShows.tv.map(sanitizeSelectedShow);
    const episodes = uniqBy(
      [
        ...selectedTvShows.episodes.map(sanitizeSelectedShow),
        ...flatten(map(selectedTvShows.seasons, 'children')).map(sanitizeSelectedShow),
      ],
      'value'
    );
    const segment = {
      ...tvShowsInput,
      type: 'advancedTvShows',
      genres: selectedGenres,
      networks: selectedNetworks,
      tv,
      episodes,
      ...(selectedStartDate && !isTimeframeDisabled && { startDate: dateToString(selectedStartDate) }),
      ...(selectedEndDate && !isTimeframeDisabled && { endDate: dateToString(selectedEndDate) }),
      ...(selectedTimeframe && { timeframe: selectedTimeframe }),
      exposure: levelOfExposure,
      viewershipModes,
      viewEventQual: viewEventQualifier,
      minViewedProgs: minNumberOfViewedProgs,
      viewingType,
      ...(isUnifiedTv ? { sources: map(selectedSources, 'value') } : {}),
    };
    if (!selectedTimeframe) {
      delete segment.timeframe;
    }
    if (isUnifiedTv) {
      delete segment.viewershipModes;
    }
    const segmentWithoutEmptyTvFilters = omitBy(
      segment,
      (val, key) => ['networks', 'genres', 'tv', 'episodes'].includes(key) && isEmpty(val)
    );
    onSubmit(segmentWithoutEmptyTvFilters);
  }, [
    tvShowsInput,
    selectedTvShows,
    selectedGenres,
    selectedNetworks,
    selectedStartDate,
    selectedEndDate,
    selectedTimeframe,
    levelOfExposure,
    viewershipModes,
    viewEventQualifier,
    minNumberOfViewedProgs,
    viewingType,
    selectedSources,
    isUnifiedTv,
  ]);

  const handleCancel = () => onCancel('advancedTvShows');

  const isSubmitDisabled = useMemo(() => {
    if (isLoadingFirstBatch) return true;
    return every(
      [selectedTvShows.tv, selectedTvShows.seasons, selectedTvShows.episodes, selectedNetworks, selectedGenres],
      isEmpty
    );
  }, [selectedTvShows, selectedNetworks, selectedGenres, isLoadingFirstBatch]);

  const loadingMoreSpinner = isLoadingMoreBatches ? (
    <img
      src={smallSpinnerGif}
      alt="Loading..."
      style={{ width: '52px', height: '15px', display: 'block', margin: '8px auto' }}
    />
  ) : null;

  const searchEmptyContent = isSearchMode ? (
    isSearching ? (
      <div className="tv-shows-search-loading">
        <img src={smallSpinnerGif} alt="Searching..." style={{ width: '52px', height: '15px' }} />
      </div>
    ) : searchTreeData !== null && searchTreeData.length === 0 ? (
      <div className="tv-shows-no-results">No results found.</div>
    ) : null
  ) : null;

  return (
    <div className="advanced-tv-shows-modal-component">
      <ModalWithConfirmationButtons
        width="1080px"
        height={MODAL_HEIGHT}
        modalTitle={modalTitle}
        isOpen={isOpen}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onXClick={handleCancel}
        isSubmitDisabled={isSubmitDisabled}
        isDisabledEnterKeyPress={isSubmitDisabled}
        isLightTheme
        hasScrollContainer={false}
        containerPadding="24px 0px"
      >
        <div className="advanced-tv-shows-modal-content">
          {hasApiError && (
            <div className="tv-shows-api-error">
              An error occurred. Please try again later or contact support if the issue persists.
            </div>
          )}
          <div className="tv-shows-split">
            <div className="tv-shows-left">
              {isUnifiedTv && (
                <div className="modal-row viewing-source">
                  <div className="row-label">Viewing Source:</div>
                  <div className="viewing-source-control" ref={sourceMenuRef}>
                    <ClickableIcon
                      iconId="filter"
                      className="viewing-source-filter"
                      onClick={() => setIsSourceMenuOpen((open) => !open)}
                    />
                    <div className="source-chips">
                      {selectedSources.map((source) => (
                        <div key={source.value} className={`source-chip ${source.colorClass}`}>
                          <span className="source-chip-label">{source.label}</span>
                          {selectedSources.length > 1 && (
                            <button
                              type="button"
                              className="source-chip-remove"
                              onClick={() => onRemoveSource(source.value)}
                            >
                              <Icon name="Remove" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {isSourceMenuOpen && (
                      <div className="viewing-source-menu">
                        {UNIFIED_TV_SOURCES.map((source) => {
                          const isSelected = selectedSources.some((s) => s.value === source.value);
                          return (
                            <div
                              key={source.value}
                              className={`viewing-source-menu-item${isSelected ? ' selected' : ''}`}
                              onClick={() => onToggleSource(source.value)}
                            >
                              <span className="viewing-source-menu-label">{source.label}</span>
                              {isSelected && (
                                <LocalIcon
                                  iconId="v"
                                  className="viewing-source-menu-check"
                                  width="12px"
                                  height="12px"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {(isHisense || isUnifiedTv || viewingType !== 'streaming') && (
                <div className="modal-row viewing-row">
                  {(isHisense || isUnifiedTv) && (
                    <div className="viewing-type">
                      <div className="row-label">Viewing Type:</div>
                      <div className="viewing-type-selector">
                        <SingleSelectToggle
                          values={viewingTypeOptions}
                          selectedValue={viewingType}
                          onToggle={onChangeViewingType}
                          isModalTheme
                        />
                      </div>
                    </div>
                  )}
                  {!isUnifiedTv && viewingType !== 'streaming' && (
                    <div className="viewership-mode">
                      <div className="row-label">Viewership Mode:</div>
                      <Dropdown
                        values={VIEWERSHIP_MODES}
                        selectedValues={viewershipModes}
                        isMulti
                        summaryTextBuilder={getSummaryTextBuilder('All viewership modes')}
                        onSelect={setViewershipModes}
                        isDisabled={isHisenseGermanyLinear || isHisenseGermanyBoth}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="modal-row networks">
                <DropdownWithHeader
                  promise={tvNetworksMetadataPromise}
                  selectedValues={selectedNetworks}
                  isMulti
                  isSearchable
                  summaryTextBuilder={getSummaryTextBuilder('All networks')}
                  onSelect={onNetworksSelect}
                  maxNumberOfSelectedOptions={7}
                  reachMaxSelectedOptionsMsg="Sorry, this field is limited to 7 networks"
                  headerTitle="Networks:"
                  showInlineClear
                />
              </div>
              <div className="modal-row genres">
                <DropdownWithHeader
                  promise={tvGenresMetadataPromise}
                  selectedValues={selectedGenres}
                  isMulti
                  isSearchable
                  summaryTextBuilder={getSummaryTextBuilder('All genres')}
                  onSelect={onGenresSelect}
                  headerTitle="Genres:"
                  showInlineClear
                  isDisabled={isHisenseGermanyLinear || isHisenseGermanyBoth}
                />
              </div>
              <div className="modal-row tv-shows">
                <div className="tv-shows-header">
                  <div className="header-title-left">TV Shows:</div>
                  <div
                    className={`header-title-right${selectedTvShowsSections.length === 0 ? ' disabled' : ''}`}
                    onClick={() => selectedTvShowsSections.length > 0 && onResetSelectedTvShows()}
                  >
                    Clear Selected
                  </div>
                </div>
                {isLoadingFirstBatch && (
                  <div className="tv-shows-loading-placeholder">
                    <img
                      src={smallSpinnerGif}
                      alt="Loading..."
                      style={{ width: '52px', height: '15px', margin: '16px auto' }}
                    />
                  </div>
                )}
                {!isLoadingFirstBatch && (
                  <MultiLevelTableSelector
                    levelNames={LEVEL_NAMES}
                    tableData={displayTreeData}
                    onChange={onShowsDataTableSelect}
                    showLevelSelector={false}
                    onScrolledToBottom={handleScrolledToBottom}
                    footerContent={loadingMoreSpinner}
                    isSearchDisabled={isLoadingFirstBatch}
                    searchInputValue={searchValue}
                    externalSearchValue={activeSearchValue}
                    onSearchQueryChange={handleSearchChange}
                    minSearchChars={MIN_SEARCH_CHARS}
                    emptyContent={searchEmptyContent}
                    showSearchScope
                    searchScope={searchScope}
                    onSearchScopeChange={handleSearchScopeChange}
                    showExactMatch
                    exactMatch={exactMatch}
                    onExactMatchChange={handleExactMatchChange}
                    showClearSearch
                    onClearSearch={handleClearSearch}
                    collapseOnParentMatch
                    forceParentIndeterminate={isSearchMode && searchScope !== 'series'}
                  />
                )}
              </div>
              <div className="modal-row timeframe">
                <div className="row-label">Timeframe:</div>
                <TvCustomTimeframe
                  startDateString={selectedStartDate}
                  endDateString={selectedEndDate}
                  timeframe={selectedTimeframe}
                  selectedStartDateCB={(selectedDate) => setSelectedStartDate(dateToString(selectedDate))}
                  selectedEndDateCB={(selectedDate) => setSelectedEndDate(dateToString(selectedDate))}
                  selectedTimeframeCB={setSelectedTimeframe}
                  isDisabled={isTimeframeDisabled || isHisenseGermanyLinear || isHisenseGermanyBoth}
                ></TvCustomTimeframe>
                <Separator className="modal-bottom-separator" />
              </div>
              <div className="modal-row level-of-exposure">
                <div className="row-label">
                  <span className="label">Level of Exposure:</span>
                  <ClickableIcon iconId="info-italic" tooltip={LEVEL_OF_EXPOSURE_TV_SHOWS_TOOLTIP} />
                </div>
                <div className="level-of-exposure-values">
                  <SelectToggle
                    values={LEVELS_OF_EXPOSURE}
                    selectedValues={levelOfExposure}
                    onChange={setLevelOfExposure}
                    isDisabled={isHisenseGermanyLinear || isHisenseGermanyBoth}
                  />
                </div>
              </div>
              <div className="modal-row exposure-row">
                {channel !== 'peerlogix' && (
                  <div className="exposure-row-item viewing-event-qualifier">
                    <div className="row-label">Viewing Event Qualifier:</div>
                    <Dropdown
                      values={VIEWING_EVENT_QUALIFIERS}
                      selectedValue={viewEventQualifier}
                      onSelect={setViewEventQualifier}
                      isDisabled={isHisenseGermanyLinear || isHisenseGermanyBoth}
                      menuMaxHeight={400}
                    />
                  </div>
                )}
                <div className="exposure-row-item number-of-objects">
                  <div className="row-label">Minimum Number of Programs Viewed:</div>
                  <div className="number-of-objects-dropdowns">
                    <span className="number-input">
                      <input
                        type="number"
                        pattern={NUM_PROGS_PATTERN}
                        value={minNumberOfViewedProgs}
                        onChange={onMinNumProgsChange}
                        onWheel={onMinNumProgsChange}
                        min={MIN_VIEWED_PROGRAMS}
                        max={MAX_VIEWED_PROGRAMS}
                        disabled={isHisenseGermanyLinear || isHisenseGermanyBoth}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tv-shows-right">
              <div className="selected-box">
                <div className="selected-box-header">
                  <div className="header-title-left">Selected</div>
                  <div
                    className={`header-title-right${
                      isEmpty(resultsTable) && isEmpty(selectedTvShowsSections) ? ' disabled' : ''
                    }`}
                    onClick={() =>
                      (!isEmpty(resultsTable) || !isEmpty(selectedTvShowsSections)) && onResetResultsTable()
                    }
                  >
                    Clear All
                  </div>
                </div>
                <div className="selected-box-content">
                  {isEmpty(resultsTable) && isEmpty(selectedTvShowsSections) && (
                    <div className="selected-box-empty">
                      <div>No items selected,</div>
                      <div>Your selections will appear here</div>
                    </div>
                  )}
                  <ResultsTable
                    resultsTable={resultsTable}
                    onRemove={onRemoveResultsTable}
                    onReset={onResetResultsTable}
                  />
                  <div className="multi-level-results-component">
                    <div className="multi-level-results">
                      {selectedTvShowsSections.map(({ level, title, items }) => (
                        <div key={level} className="mlr-result">
                          <div className="mlr-result-header">{title}</div>
                          {items.map((item) => {
                            const secondaryText = getSelectedShowSecondaryText(level, item);
                            return (
                              <div key={item.selectionKey} className="mlr-result-child">
                                <div className="mlr-result-child-title">
                                  {isUnifiedTv && item.source && (
                                    <span className={`tv-source-dot ${getSourceColorClass(item.source)}`} />
                                  )}
                                  {item.label}
                                </div>
                                {!!secondaryText && <div className="mlr-result-child-body">{secondaryText}</div>}
                                <div
                                  className="mlr-result-child-action"
                                  onClick={() => onRemoveSelectedTvShow(level, item)}
                                >
                                  <Icon name="Remove" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalWithConfirmationButtons>
    </div>
  );
};

AdvancedTvShowsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  tvShowsInput: PropTypes.object,
  tvGenresMetadataPromise: PropTypes.object,
  channel: PropTypes.string,
  geo: PropTypes.string,
};

AdvancedTvShowsModal.defaultProps = {
  tvShowsInput: {},
  channel: '',
  geo: '',
};

export default AdvancedTvShowsModal;
